import { Alert, AlertColor } from '@mui/material';
import { useAppDispatch } from '../../store';
import { clearAnnouncement } from './AnnouncementSlice';
import { useEffect, useState } from 'react';
import { AnnouncementActionTypes } from './types';
import AnnouncementAction from './AnnouncementAction';

import styles from './Announcement.module.css';

export interface AnnouncementProps {
	uniqueId: number;
	/** Message to display on the Alert */
	title: string;
	/** Defines icon and color set to use */
	severity?: AlertColor;
	/** Action to render that displays right-justified on the Alert */
	action?: AnnouncementActionTypes;
	/** When true and no action is present, displays a right-justified Close Icon to remove the Alert */
	clearable?: boolean;
	/** Announcement will automatically clear itself after the default timeout */
	autoClear?: boolean;
	/** When true, will not use the border-radius */
	straightEdges?: boolean;
	/** When true, will override the theme to force the filled variant */
	filled?: boolean;
}

/**
 * Component for displaying a message to the user
 */
export default function Announcement(props: AnnouncementProps) {
	const appDispatch = useAppDispatch();

	const [dismiss, setDismiss] = useState(false);

	const handleClose = () => {
		setDismiss(true);
	};

	const { uniqueId, autoClear } = props;
	useEffect(() => {
		if (dismiss) {
			const timeout = setTimeout(() => {
				appDispatch(clearAnnouncement(uniqueId));
			}, 200);

			return () => {
				clearTimeout(timeout);
			};
		}
	}, [dismiss, appDispatch, uniqueId]);

	useEffect(() => {
		if (!autoClear) {
			return;
		}

		const autoClearTimeout = setTimeout(() => {
			setDismiss(true);
		}, 8000);

		return () => {
			clearTimeout(autoClearTimeout);
		};
	}, [uniqueId, autoClear, appDispatch]);

	return (
		<Alert
			elevation={0}
			severity={props.severity}
			onClose={props.clearable ? handleClose : undefined}
			className={`${styles.announcement} ${dismiss ? styles.dismiss : styles.show} ${props.straightEdges ? styles.straight : ''}`}
			sx={{
				padding: '0px 8px',
				'& .MuiAlert-icon': {
					padding: '5px 0px'
				},
				'& .MuiAlert-message': {
					padding: '6px 0px',
					overflowY: 'hidden'
				},
				'& .MuiAlert-action': {
					padding: '2px 0 0 16px',
					marginRight: '0px'
				}
			}}
			variant={props.filled ? 'filled' : undefined}
			action={props.action && <AnnouncementAction action={props.action} />}
		>
			{props.title}
		</Alert>
	);
}
